﻿import { useState, useEffect } from 'react';
import { SecurelyGetProducerInformation } from '../../../api/admin';
import { GetProducerIDsFromNPN } from '../../../api/regedint';
import { useQuery } from '@tanstack/react-query';
import PersonIcon from '@mui/icons-material/Person';
import {
  FormControl,
  InputLabel,
  Select,
  TextField,
  MenuItem,
  Button,
  Box,
  Modal,
  Stack,
  Chip,
} from '@mui/material';
import GenericDialog from '../../../components/Dialog/GenericDialog';
import { styled } from '@mui/material/styles';
import { useUser } from '../../../context/UserContext';

export default function SelectProducer({
  setNpnUpdateDetails,
  setSelectProducerOpen,
  showAbbrvProducer = false,
  showLabel = false,
  selectLabel = 'Select a Producer',
  variant = 'filled',
  showActiveOnly = true,
  selectBackgroundColor,
  textColor,
  ...props
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mainModalOpen, setMainModalOpen] = useState(false);
  const [currentProducer, setCurrentProducer] = useState(null);
  const [producer, setProducer] = useState('');
  const [producerID, setProducerID] = useState('');
  const [producerData, setProducerData] = useState([]);
  const [filter, setFilter] = useState('');
  const user = useUser();
  const [producerHelperText, setProducerHelperText] = useState('');
  const [producerError, setProducerError] = useState(false);
  const [producersDropdownOptions, setProducersDropdownOptions] = useState([]);
  const [showDownlines, setShowDownlines] = useState(false);

  const addProducersDropdownOption = (pID) => {
    setProducersDropdownOptions((prevItems) => [...prevItems, pID]);
  };

  useEffect(() => {
    if (producersDropdownOptions.length > 0) {
      setCurrentProducer(producersDropdownOptions[0]);
      setSelectedIndex(0);
    }
  }, [producersDropdownOptions]);

  const getProducerIDsFromNPN = useQuery({
    queryKey: ['GetProducerIDsFromNPN', user.npnID],
    queryFn: () => GetProducerIDsFromNPN({ NPN: user.npnID, Status: '', State: '' })(),
    enabled: user != null && !user.isHO,
  });

  const StyledTextField = styled(TextField)({
    '& .MuiOutlinedInput-input': {
      padding: '0.5em',
    },
  });

  //handler functions
  const handleOpen = () => {
    setFilter('');
    setProducer('');
    setMainModalOpen(true);
    if (setSelectProducerOpen) {
      setSelectProducerOpen(true);
    }
  };

  const handleClose = () => {
    if (mainModalOpen) {
      setMainModalOpen(false);
    }
    if (setSelectProducerOpen) {
      setSelectProducerOpen(false);
    }
  };

  useEffect(() => {
    if (currentProducer !== null) {
      setProducer(currentProducer);
      setNpnUpdateDetails([
        { name: 'NPN', newValue: currentProducer?.ProducerId },
        { name: 'ShowDownlines', newValue: true },
      ]);
      handleClose();
    }
  }, [currentProducer]);

  const handleHOProducerChange = () => {
    var pID = document.getElementById('select-a-producer-powerbi').value;

    if (pID !== '' && pID.length >= 9) {
      setProducerError(false);
      setProducerHelperText('');
      setProducer(pID);
      setProducerID(pID);
      setNpnUpdateDetails([
        { name: 'NPN', newValue: pID },
        { name: 'ShowDownlines', newValue: true },
      ]);
    } else {
      setProducerError(true);
      setProducerHelperText('Invalid Override Producer ID Length');
    }
  };

  //Hooks
  const secureInfo = useQuery({
    queryKey: ['producerInfo', filter],
    queryFn: () => SecurelyGetProducerInformation({ QueryString: filter })(),
    enabled:
      user != null &&
      !user.isHO &&
      getProducerIDsFromNPN.data != null &&
      !getProducerIDsFromNPN.isPending &&
      !getProducerIDsFromNPN.isFetching,
  });

  useEffect(() => {
    if (secureInfo.isSuccess && secureInfo.data) {
      var pData = JSON.parse(secureInfo.data);
      setProducerData(Array.isArray(pData) ? pData : []);

      if (currentProducer === null) {
        let pr = null;
        let dta = getProducerIDsFromNPN.data;

        if (dta != null && dta.length > 1) {
          for (var p = 0; p < dta.length; p++) {
            for (var i = 0; i < pData.length; i++) {
              if (pData[i].ProducerId.trim() === user.producerID.trim() && pr == null) {
                pr = pData[i];
              }

              if (pData[i].ProducerId.trim() === dta[p].trim()) {
                addProducersDropdownOption(pData[i]);
                break;
              }
            }
          }
        } else {
          pr = pData.find((el) => el.ProducerId.trim() === dta[0].trim());
        }

        //add downline option if we have more than the producer length
        if (pData.length > dta.length) {
          //and add the single contract # if only one is available
          if (dta.length === 1) addProducersDropdownOption(pr);

          setShowDownlines(true);
        }

        setCurrentProducer(pr);
      }
    }
  }, [secureInfo.isSuccess, secureInfo.data]);

  return (
    <>
      {user.npnID === 'HO' && (
        <div className="d-flex" style={{ width: '100%', margin: '0.5em 0' }}>
          <Stack direction="row" spacing={1} sx={{ height: 'fit-content' }}>
            <StyledTextField
              fullWidth
              key="select-a-producer-textfield"
              variant="outlined"
              defaultValue={producerID}
              margin="none"
              size="small"
              id="select-a-producer-powerbi"
              placeholder="Producer ID Override"
              error={producerError}
              helperText={producerHelperText}
            />
            <Button
              sx={{ height: 'fit-content' }}
              margin="none"
              size="small"
              disabled={false}
              variant="contained"
              color="primary"
              onClick={() => {
                handleHOProducerChange();
              }}
            >
              View
            </Button>
          </Stack>
        </div>
      )}

      {currentProducer === null ? (
        ''
      ) : (
        <Box sx={{ paddingBottom: '1rem', ...(props.containerSx || '') }}>
          {user.npnID !== 'HO' && (
            <div className="d-flex" style={{ gap: '10px' }}>
              <>
                {producersDropdownOptions.length > 0 && props.useLabel && (
                  <div style={{ fontWeight: '500', alignContent: 'center', margin: '0 0 0 0.5em' }}>
                    Select Producer:
                  </div>
                )}
                {producersDropdownOptions.length === 0 ? (
                  <Chip
                    label={currentProducer?.PrintName}
                    icon={<PersonIcon />}
                    variant="outlined"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  />
                ) : (
                  <FormControl variant={variant} sx={{ ...(props.formControlSx || '') }}>
                    {showLabel && <InputLabel id="producer-select-label">Producer IDs</InputLabel>}
                    <Select
                      labelId={showLabel ? 'producer-select-label' : undefined}
                      id="producer-select"
                      value={currentProducer?.ProducerId || ''}
                      onChange={(event) => {
                        const selectedProducer = producersDropdownOptions
                          .filter((option) =>
                            props.showActiveOnly ? option.ContractStatus === 'A' : true
                          )
                          .find((p) => p.ProducerId === event.target.value);

                        if (selectedProducer) {
                          const index = producersDropdownOptions.findIndex(
                            (p) => p.ProducerId === event.target.value
                          );
                          setSelectedIndex(index);
                          setCurrentProducer(selectedProducer);
                        }
                      }}
                      renderValue={(value) => {
                        return showAbbrvProducer
                          ? currentProducer?.ProducerId
                          : currentProducer?.PrintName;
                      }}
                      label={showLabel ? selectLabel : ''}
                      disabled={producersDropdownOptions.length === 0}
                      size={props.selectSize || 'medium'}
                      startAdornment={
                        currentProducer !== null && props.useIcon ? (
                          <PersonIcon
                            sx={{ color: textColor || 'inherit' }}
                            style={{ marginRight: '0.5em' }}
                          />
                        ) : null
                      }
                      sx={{
                        backgroundColor: selectBackgroundColor || '',
                        color: textColor || 'inherit',
                        '&:hover': {
                          backgroundColor: selectBackgroundColor || '',
                        },
                        '&.Mui-focused': {
                          backgroundColor: selectBackgroundColor || '',
                        },
                        '&::before, &::after': {
                          backgroundColor: 'transparent',
                        },
                        ...props.selectSx,
                      }}
                    >
                      {producersDropdownOptions
                        .filter((option) =>
                          props.showActiveOnly ? option.ContractStatus === 'A' : true
                        )
                        .map((option, index) => (
                          <MenuItem
                            key={option.ProducerId + ' - ' + index}
                            value={option.ProducerId}
                            style={{ display: 'block', padding: '0.35em 1em' }}
                          >
                            {showAbbrvProducer ? option.ProducerId : option.PrintName}
                          </MenuItem>
                        ))}
                      {showDownlines && (
                        <MenuItem onClick={handleOpen} value="Select a Downline">
                          Select a Downline
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}
              </>
            </div>
          )}
        </Box>
      )}
      <GenericDialog
        open={mainModalOpen}
        onClose={handleClose}
        showDialogActions={false}
        showDialogTitle={false}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            maxWidth: '400px',
            margin: 'auto',
          },
        }}
        content={
          <div className="d-grid gap-4">
            <div className="my-2">
              <TextField
                fullWidth
                id="filled-basic"
                label="Filter by name or producer ID"
                value={filter}
                variant="filled"
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            <div className="row">
              {!producerData.length ? (
                <>Loading</>
              ) : (
                <TextField
                  value={producer}
                  onChange={(evt) => {
                    setCurrentProducer(evt.target.value);
                  }}
                  select
                  fullWidth
                  id="agent-select"
                  label="Producers"
                >
                  {producerData.map((e, i) => {
                    return (
                      <MenuItem key={i} value={e}>
                        {e.PrintName.trim()}
                      </MenuItem>
                    );
                  })}
                </TextField>
              )}
            </div>
          </div>
        }
      />

      {/*<Modal*/}
      {/*    open={producerModalOpen}*/}
      {/*    onClose={handleCloseProducerModal}*/}
      {/*    aria-labelledby="modal-multiple-producer-ids"*/}
      {/*    aria-describedby="modal-multiple-producer-ids"*/}
      {/*>*/}
      {/*    <Box sx={style}>*/}
      {/*        <>*/}
      {/*            <div>The agent you selected has multiple producer Ids, please select one from the list below.</div>*/}
      {/*            <div className="d-grid gap-4">*/}
      {/*                <TextField id="filled-basic" label="Filter by agent or agency" value={filter} variant="filled" onChange={(e) => setFilter(e.target.value)} />*/}
      {/*                <div className="row">{*/}
      {/*                    usrLst === null ? <>Loading</> :*/}
      {/*                        <FormControl fullWidth className="col">*/}
      {/*                            <InputLabel id="producerIds-select-label">ProducerIds</InputLabel>*/}
      {/*                            <Select*/}
      {/*                                labelId="producerIds-select-label"*/}
      {/*                                id="producer-select"*/}
      {/*                                value={selectedProducerId}*/}
      {/*                                onChange={handleProducerIdChange}*/}
      {/*                                label="Producer Ids"*/}
      {/*                            >*/}
      {/*                                {selectedUserProducerIds.map((e, i) => {*/}
      {/*                                    return <MenuItem key={`producer-${i}`} value={e}>{e}</MenuItem>*/}
      {/*                                })*/}

      {/*                                }*/}
      {/*                            </Select>*/}
      {/*                        </FormControl>*/}
      {/*                }</div>*/}
      {/*            </div>*/}
      {/*        </>*/}
      {/*    </Box>*/}
      {/*</Modal>*/}
    </>
  );
}
